// HTML-string helpers for the Leaflet marker icons, and the rings' keyframes (added
// to the page only when asked, a no-op without a DOM). No React or leaflet import,
// so its test (Vitest, node environment, no DOM) can load this file.

/** A twin's location mode, which picks its marker silhouette. */
export type LocationModeTag = 'Static' | 'Dynamic' | 'None';

/** CSS clip-path polygon for a regular hexagon (the Dynamic silhouette). A host
 * reuses it for lists and legends that must match the marker exactly. */
export const HEX_CLIP = 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)';

/** CSS clip-path polygon for a diamond (the None silhouette).
 * Lives alongside HEX_CLIP so both location-mode silhouettes share one home. */
export const DIAMOND_CLIP = 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)';

// Escape user-controlled strings before interpolating into raw-HTML Leaflet
// icons and tooltips (twin ids and model names are user input, so an unescaped
// one is stored XSS). Quotes are escaped too, so element text and a double-quoted
// attribute are safe; never a URL context. A missing value (an unexpected row
// shape) renders empty instead of throwing inside a Leaflet redraw.
export const escapeHtml = (s: string | null | undefined): string =>
  (s ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );

// The selected-marker colors.
export const MARKER_SELECTION = {
  selected: '#FF5722', // the selection color of every twin view
  selectedGlow: '#FF572240', // 25% alpha halo for the selected marker
} as const;

/** The animation the selection and pick rings pulse with. */
export const MARKER_PULSE = 'tt-map-pulse';

const MARKER_PULSE_STYLE_ID = 'tt-map-pulse-style';
const MARKER_PULSE_KEYFRAMES = `@keyframes ${MARKER_PULSE}{0%{transform:scale(1);opacity:0.6}70%{transform:scale(1.8);opacity:0}100%{transform:scale(1.8);opacity:0}}`;

/**
 * Adds the rings' `@keyframes` to the page, once: a `<style id="tt-map-pulse-style">`
 * in its head, so a host defines no CSS for them. Does nothing without a DOM.
 */
export function ensureMarkerPulseKeyframes(doc: Document | undefined = globalThis.document): void {
  if (!doc?.head || doc.getElementById(MARKER_PULSE_STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = MARKER_PULSE_STYLE_ID;
  style.textContent = MARKER_PULSE_KEYFRAMES;
  doc.head.appendChild(style);
}

// Marker icons are built as HTML strings for Leaflet's L.divIcon, so a thumbnail
// URL is interpolated into an inline `background-image:url('...')` inside a
// double-quoted style attribute. Reject any URL that isn't a plain http(s)/data
// image URL or that contains characters which could break out of that context
// (quotes, parens, angle brackets, backslash, whitespace/control chars), to
// prevent HTML/CSS injection from a malicious asset thumbnail URL. Rejected
// URLs fall back to the plain colored marker shape. A React `src` attribute
// escapes the URL itself and needs no such guard.
export function safeMarkerThumbnailUrl(url?: string): string | undefined {
  if (!url) return undefined;
  if (!/^(https?:\/\/|data:image\/)/i.test(url)) return undefined;
  // eslint-disable-next-line no-control-regex -- intentionally reject control chars
  if (/['"()<>\\\s\x00-\x1f]/.test(url)) return undefined;
  return url;
}

export interface MarkerInnerHtmlOpts {
  mode: LocationModeTag;
  size: number;
  fill: string;
  stroke: string;
  isSelected: boolean;
  /** Must come from safeMarkerThumbnailUrl: it goes unescaped into an inline style attribute. */
  thumbnailUrl?: string;
}

export function markerInnerHtml({
  mode,
  size,
  fill,
  stroke,
  isSelected,
  thumbnailUrl,
}: MarkerInnerHtmlOpts): string {
  const opacity = isSelected ? 1 : 0.9;
  const strokeWidth = isSelected ? 3 : 2;
  const strokeColor = isSelected ? MARKER_SELECTION.selected : stroke;
  const shadow = isSelected
    ? `box-shadow:0 0 0 4px ${MARKER_SELECTION.selectedGlow},0 1px 6px rgba(0,0,0,0.55);`
    : 'box-shadow:0 1px 3px rgba(0,0,0,0.4);';
  // clip-path doesn't follow box-shadow, so the hexagon/diamond shapes use
  // filter:drop-shadow on the outer wrapper instead.
  const dropShadow = isSelected
    ? `filter:drop-shadow(0 0 3px ${MARKER_SELECTION.selectedGlow}) drop-shadow(0 1px 3px rgba(0,0,0,0.55));`
    : 'filter:drop-shadow(0 1px 2px rgba(0,0,0,0.4));';
  // Border-bounded shapes (circle, diamond-without-thumbnail) share this tail.
  const sharedTail = `background:${fill};border:${strokeWidth}px solid ${strokeColor};${shadow}opacity:${opacity}`;
  // When the model has an associated 3D thumbnail, the inner shape is filled
  // with the image instead of a flat color; the outer colored shape still
  // shows through as a ring, preserving the model color.
  // A black backing color sits behind the thumbnail image so transparent
  // regions of the PNG render black rather than showing the map (circle) or
  // the colored ring layer (hexagon/diamond) through them.
  const innerFill = thumbnailUrl
    ? `background-color:#000;background-image:url('${thumbnailUrl}');background-size:cover;background-position:center;`
    : `background:${fill};`;

  // Hexagon (Dynamic) and diamond (None-with-thumbnail) both use a clip-path:
  // an outer colored shape with an inset inner shape carrying the fill/image.
  // clip-path doesn't clip a border, so the colored ring comes from the inset.
  const clipShape = (clipPath: string): string => {
    const inset = isSelected ? 2.5 : 1.5; // 1.5px avoids the 1px anti-alias seam at 2px
    return `<div style="position:relative;width:${size}px;height:${size}px;background:${strokeColor};clip-path:${clipPath};${dropShadow}opacity:${opacity}"><div style="position:absolute;inset:${inset}px;${innerFill}clip-path:${clipPath}"></div></div>`;
  };

  if (mode === 'Dynamic') {
    return clipShape(HEX_CLIP);
  }

  if (mode === 'None') {
    // The thumbnail variant clips to a diamond; the plain variant stays a
    // cheaper bordered rotated square.
    if (thumbnailUrl) return clipShape(DIAMOND_CLIP);
    return `<div style="width:${size}px;height:${size}px;${sharedTail};transform:rotate(45deg)"></div>`;
  }

  // Static (circle). With a thumbnail, background-clip:padding-box keeps the
  // image inside the colored border ring rather than bleeding under it.
  const circleFill = thumbnailUrl
    ? `${innerFill}background-clip:padding-box;${shadow}opacity:${opacity}`
    : sharedTail;
  const circleBorder = thumbnailUrl ? `border:${strokeWidth}px solid ${strokeColor};` : '';
  return `<div style="width:${size}px;height:${size}px;border-radius:50%;${circleBorder}${circleFill}"></div>`;
}
